import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faCircleQuestion, faCircleXmark, faMinus, faSpinner, faTriangleExclamation } from '@/fontawesome';
import { refCheckLabel } from '@/utils/symbol-source';
import type { RefCheckState, SymbolPriceSource } from '@/utils/symbol-source';

const GRAY = 'text-gray-500 dark:text-gray-400';

const VISUALS: Record<RefCheckState['kind'], { icon: IconDefinition; color: string }> = {
  checking: { icon: faSpinner, color: GRAY },
  empty: { icon: faMinus, color: GRAY },
  unchecked: { icon: faCircleQuestion, color: GRAY },
  found: { icon: faCircleCheck, color: 'text-green-600 dark:text-green-400' },
  candidate: { icon: faCircleCheck, color: 'text-blue-600 dark:text-blue-400' },
  missing: { icon: faTriangleExclamation, color: 'text-orange-600 dark:text-orange-400' },
  invalid: { icon: faCircleXmark, color: 'text-red-600 dark:text-red-400' },
  error: { icon: faCircleXmark, color: 'text-red-600 dark:text-red-400' },
};

interface Props {
  source: SymbolPriceSource;
  state: RefCheckState;
  /** Replaces the default label of the state. */
  text?: string;
  /** Icon only, the text becomes its tooltip. */
  compact?: boolean;
}

/** Verification state of a typed Google-Sheets/EODHD ref. */
export default function RefCheckIndicator({ source, state, text, compact = false }: Props) {
  const label = text ?? refCheckLabel(source, state);
  const { icon, color } = VISUALS[state.kind];
  const iconClass = `w-3 h-3 shrink-0 ${state.kind === 'checking' ? 'animate-spin' : ''}`;

  if (compact) {
    return (
      <span className={`inline-flex shrink-0 ${color}`} title={label}>
        <FontAwesomeIcon icon={icon} className={iconClass} />
      </span>
    );
  }

  return (
    <p className={`mt-1 flex items-center gap-1.5 text-xs ${color}`}>
      <FontAwesomeIcon icon={icon} className={iconClass} />
      <span className="truncate" title={label}>{label}</span>
    </p>
  );
}
